import { Card, CardContent, Typography, Box, Divider, Link, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AccountCircle as AccountIcon, Person as PersonIcon, Schedule as BillingIcon } from "@mui/icons-material";
import styled from '@emotion/styled';
import { shape } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import UserMenuChip from './UserMenuChip';
import ActiveChip from './ActiveChip';
import MarkdownDisplay from './MarkdownDisplay';
import { statusColor } from '../utils/statusColors';

const CardStyled = styled(Card, { shouldForwardProp: (prop) => prop !== 'inactive' })(({ theme, inactive }) => ({
    position: "relative",
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[3],
    borderRadius: shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "visible",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.light,
    },
    ...(inactive && {
        borderLeft: `6px solid ${theme.palette.warning.main}`,
        background: alpha(theme.palette.warning.main, 0.04),
    }),
    // "&::before": {
    //     content: '""',
    //     position: "absolute",
    //     top: 0,
    //     left: shape.borderRadius,
    //     right: shape.borderRadius,
    //     height: 4,
    //     background: "linear-gradient(90deg, #FFFFFF, #F7951B)",
    //     borderRadius: shape.borderRadius * 2,
    // },
}));

const AccountAvatarBox = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0),
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& .MuiSvgIcon-root": {
        fontSize: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
}));

const AccountName = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h6.fontSize,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const SecondaryInfo = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
    textDecoration: "none",
    "&:hover": {
        color: theme.palette.text.primary,
        textDecoration: "underline",
    },
}));

const DividerStyled = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(1, 0),
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const MetricsBox = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const MetricBox = styled(Box)(({ theme }) => ({
    background: alpha(theme.palette.primary.main, 0.05),
    borderRadius: 12,
    padding: "12px 16px",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
}));

const MetricValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.lg,
    color: theme.palette.text.primary,
    marginBottom: 2,
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: theme.typography.fontWeightMedium,
}));

const ClientInactiveChip = styled(Chip)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const StatusBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(2),
    minWidth: theme.spacing(2),
    height: theme.spacing(2),
    borderRadius: "50%",
    [theme.breakpoints.up("sm")]: {
        width: theme.spacing(2),
        height: theme.spacing(2),
    },
}));

const AccountCard = ({ account, HandleReAssignAccountManager }) => {
    const { name, slug, active, status, overview, client, privacy, billingTerms, accountManager } = account;
    const navigate = useNavigate();
    const theme = useTheme();
    const clientInactive = client?.active === false;
    const isInactive = !active || clientInactive;

    return (
        <CardStyled inactive={isInactive}>
            <CardContent>
                <AccountAvatarBox>
                    {clientInactive && active && (
                        <ClientInactiveChip color="warning" variant="outlined" size="small" label="Client Inactive" />
                    )}
                    <ActiveChip status={active ? "Active" : "Inactive"} />
                </AccountAvatarBox>
                <AccountName variant="h6" onClick={() => navigate(`/account/${slug}`)}>
                    {privacy !== "Public" && <StatusBox bgcolor={statusColor(theme, "privacy", privacy)} />}
                    {name}
                </AccountName>
                <SecondaryInfo component="div">
                    <InfoRow>
                        <AccountIcon />
                        <Typography variant="subtitle2" color="textSecondary">
                            Client:{" "}
                        </Typography>
                        <StyledLink variant="subtitle2" color="textPrimary" onClick={() => client?.slug && navigate(`/client/${client.slug}`)}>
                            {client?.name || '—'}
                        </StyledLink>
                    </InfoRow>
                </SecondaryInfo>
                <SecondaryInfo component="div">
                    <InfoRow>
                        <PersonIcon />
                        <Typography variant="subtitle2" color="textSecondary">
                            Account Manager:{" "}
                        </Typography>
                        <UserMenuChip user={accountManager} menuItems={{ reAssign: true }} onReAssign={HandleReAssignAccountManager ? () => HandleReAssignAccountManager(account._id) : undefined} />
                    </InfoRow>
                </SecondaryInfo>

                <MetricsBox>
                    <MetricBox>
                        <MetricValue>{status}</MetricValue>
                        <MetricLabel>Status</MetricLabel>
                    </MetricBox>

                    <MetricBox>
                        <MetricValue>{billingTerms.type}</MetricValue>
                        <MetricLabel>Billing</MetricLabel>
                    </MetricBox>
                </MetricsBox>

                {billingTerms.type === "Retainer" && (
                    <InfoRow>
                        <BillingIcon />
                        <Typography variant="body2" color="text.secondary">
                            {billingTerms.hours} Hrs {billingTerms.frequency} / {billingTerms.overage ? "Overage Allowed" : "No Overage"}
                        </Typography>
                    </InfoRow>
                )}
                {overview !== "" && (
                    <>
                        <DividerStyled />
                        <MarkdownDisplay content={overview} />
                    </>
                )}
            </CardContent>
        </CardStyled>
    );
};

export default AccountCard;
